import type { NormalizedEvent } from "@/services/types/agent-gateway";

type ToolEvent = Extract<
  NormalizedEvent,
  { type: "tool_started" | "tool_completed" }
>;

export type ActivityPhase = {
  id:
    | "company_discovery"
    | "lead_research"
    | "decision_makers"
    | "contact_verification"
    | "approval_preparation"
    | "outreach_delivery"
    | "outreach_progress"
    | "agent_activity";
  title: string;
};

const phases: Record<ActivityPhase["id"], ActivityPhase> = {
  company_discovery: { id: "company_discovery", title: "Finding companies" },
  lead_research: {
    id: "lead_research",
    title: "Researching and qualifying leads",
  },
  decision_makers: {
    id: "decision_makers",
    title: "Finding decision-makers",
  },
  contact_verification: {
    id: "contact_verification",
    title: "Verifying contact details",
  },
  approval_preparation: {
    id: "approval_preparation",
    title: "Preparing approval",
  },
  outreach_delivery: { id: "outreach_delivery", title: "Sending outreach" },
  outreach_progress: {
    id: "outreach_progress",
    title: "Checking outreach progress",
  },
  agent_activity: { id: "agent_activity", title: "Agent activity" },
};

const phaseByTool: Record<string, ActivityPhase["id"]> = {
  initialize_research: "company_discovery",
  load_research_checkpoint: "company_discovery",
  google_places_search: "company_discovery",
  google_place_details: "company_discovery",
  search_companies: "company_discovery",
  nv_search_companies: "company_discovery",
  crawl_company_website: "lead_research",
  brave_web_search: "lead_research",
  run_obscura_discovery: "lead_research",
  search_company_jobs: "lead_research",
  submit_company_evidence: "lead_research",
  qualify_company: "lead_research",
  submit_lead_evidence: "lead_research",
  submit_company_qualification: "lead_research",
  summarize_lead: "lead_research",
  search_people_web: "decision_makers",
  search_people: "decision_makers",
  fetch_person: "decision_makers",
  fetch_company: "decision_makers",
  nv_search_people: "decision_makers",
  nv_fetch_person: "decision_makers",
  nv_fetch_company: "decision_makers",
  qualify_person: "decision_makers",
  submit_decision_maker_qualification: "decision_makers",
  verify_email_lead: "contact_verification",
  check_whatsapp_contact: "contact_verification",
  request_verified_leads_approval: "approval_preparation",
  request_outbound_action_approval: "approval_preparation",
  request_outbound_action_approvals: "approval_preparation",
  generate_outreach_template: "approval_preparation",
  send_email_via_gateway: "outreach_delivery",
  send_email_via_gmail: "outreach_delivery",
  send_linkedin_message: "outreach_delivery",
  send_linkedin_connection_request: "outreach_delivery",
  send_outreach_email: "outreach_delivery",
  send_whatsapp_outreach: "outreach_delivery",
  send_whatsapp_via_gateway: "outreach_delivery",
  message_dr_mueller_team: "outreach_delivery",
  email_dr_mueller_team: "outreach_delivery",
  check_connection_status: "outreach_progress",
  check_linkedin_message_result: "outreach_progress",
  check_whatsapp_reply_loop: "outreach_progress",
  check_email_reply_loop: "outreach_progress",
  poll_linkedin_reply_loops: "outreach_progress",
  poll_linkedin_network_acceptances: "outreach_progress",
  poll_email_reply_loops: "outreach_progress",
  get_workflow_result: "outreach_progress",
};

export function activityPhaseForTool(tool: string): ActivityPhase {
  return phases[phaseByTool[tool] ?? "agent_activity"];
}

type ActivitySummary = {
  active: ToolEvent[];
  completed: ToolEvent[];
};

function countLabel(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function completedDetail(summary: ActivitySummary) {
  const successful = summary.completed.filter(
    (event) => event.content.status === "succeeded",
  );
  const blocked = summary.completed.filter(
    (event) => event.content.status !== "succeeded",
  );
  const completedByTool = new Map<string, number>();
  for (const event of successful) {
    completedByTool.set(
      event.content.tool,
      (completedByTool.get(event.content.tool) ?? 0) + 1,
    );
  }

  const searches = completedByTool.get("google_places_search") ?? 0;
  const companiesLoaded = completedByTool.get("google_place_details") ?? 0;

  if (searches > 0 || companiesLoaded > 0) {
    return [
      searches > 0 ? countLabel(searches, "search") : null,
      companiesLoaded > 0
        ? countLabel(companiesLoaded, "company loaded", "companies loaded")
        : null,
    ].filter(Boolean);
  }

  const profiles = [
    "search_people",
    "fetch_person",
    "nv_search_people",
    "nv_fetch_person",
  ].reduce((count, tool) => count + (completedByTool.get(tool) ?? 0), 0);
  if (profiles > 0) {
    return [
      countLabel(
        profiles,
        "decision-maker researched",
        "decision-makers researched",
      ),
    ];
  }

  const contactsVerified = completedByTool.get("verify_email_lead") ?? 0;
  const whatsappChecks = completedByTool.get("check_whatsapp_contact") ?? 0;
  if (contactsVerified > 0 || whatsappChecks > 0) {
    return [
      contactsVerified > 0
        ? countLabel(contactsVerified, "email verified", "emails verified")
        : null,
      whatsappChecks > 0
        ? countLabel(
            whatsappChecks,
            "WhatsApp contact checked",
            "WhatsApp contacts checked",
          )
        : null,
    ].filter(Boolean);
  }

  const messagesSent = [
    "send_email_via_gateway",
    "send_email_via_gmail",
    "send_linkedin_message",
    "send_linkedin_connection_request",
    "send_outreach_email",
    "send_whatsapp_outreach",
    "send_whatsapp_via_gateway",
  ].reduce((count, tool) => count + (completedByTool.get(tool) ?? 0), 0);
  if (messagesSent > 0) {
    return [countLabel(messagesSent, "message sent", "messages sent")];
  }

  if (successful.length > 0) {
    return [countLabel(successful.length, "completed step", "completed steps")];
  }

  return blocked.length > 0
    ? [countLabel(blocked.length, "blocked step", "blocked steps")]
    : [];
}

export function activitySummaryLabel(summary: ActivitySummary) {
  const details = completedDetail(summary);

  if (summary.active.length > 0) {
    details.push(
      countLabel(
        summary.active.length,
        "step in progress",
        "steps in progress",
      ),
    );
  }

  return details.join(" · ");
}
